
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MasterLicenseLayout from '@/components/masterlicense/MasterLicenseLayout';
import BlogPostsTable from '@/components/blog/BlogPostsTable';
import AIContentSettings from '@/components/blog/AIContentSettings';
import AIBlogGenerationDialog from '@/components/blog/AIBlogGenerationDialog';
import BlogPostEditor from '@/components/blog/BlogPostEditor';
import BlogPostPreview from '@/components/blog/BlogPostPreview';
import BlogManagementHeader from '@/components/blog/BlogManagementHeader';
import BlogStatusTabs from '@/components/blog/BlogStatusTabs';
import { BlogPost } from '@/types/blog';
import { useBlogManagement } from '@/hooks/useBlogManagement';
import { initialBlogPosts } from '@/data/blogPosts';

const MasterLicenseBlogManagement: React.FC = () => {
  const [date, setDate] = useState<Date>();
  const [isAIDialogOpen, setIsAIDialogOpen] = useState(false);
  const [newBlogPrompt, setNewBlogPrompt] = useState('');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [currentPost, setCurrentPost] = useState<BlogPost | undefined>(undefined);
  const [editorMode, setEditorMode] = useState<'create' | 'edit' | 'view'>('create');
  
  const navigate = useNavigate();

  const {
    filteredPosts,
    searchTerm,
    isGenerating,
    handleSearch,
    filterByStatus,
    handleDelete,
    handleToggleAutoSchedule,
    handleGenerateBlog,
    handleSavePost,
  } = useBlogManagement(initialBlogPosts);

  const handleCreatePost = () => {
    setCurrentPost(undefined);
    setEditorMode('create');
    setIsEditorOpen(true);
  };
  
  const handleEditPost = (post: BlogPost) => {
    setCurrentPost(post);
    setEditorMode('edit');
    setIsEditorOpen(true);
  };
  
  const handleViewPost = (post: BlogPost) => {
    setCurrentPost(post);
    setIsPreviewOpen(true);
  };

  const handleGenerateWithAI = () => {
    handleGenerateBlog(newBlogPrompt);
    setIsAIDialogOpen(false);
    setNewBlogPrompt('');
  };

  return (
    <MasterLicenseLayout
      onLogout={() => navigate('/login')}
      activeTab="blog"
      userRole="Master Admin"
    >
      <div className="p-6">
        <BlogManagementHeader
          onAIDialogOpen={() => setIsAIDialogOpen(true)}
          onCreatePost={handleCreatePost}
          searchTerm={searchTerm}
          onSearch={handleSearch}
        />
        
        <BlogStatusTabs onStatusChange={filterByStatus} />
        
        <BlogPostsTable 
          posts={filteredPosts}
          onDelete={handleDelete}
          onToggleAutoSchedule={handleToggleAutoSchedule}
          onEdit={handleEditPost}
          onView={handleViewPost}
          onCreate={handleCreatePost}
        />
        
        <AIContentSettings />

        <AIBlogGenerationDialog
          open={isAIDialogOpen}
          onOpenChange={setIsAIDialogOpen}
          prompt={newBlogPrompt}
          onPromptChange={setNewBlogPrompt}
          date={date}
          onDateChange={setDate}
          isGenerating={isGenerating}
          onGenerate={handleGenerateWithAI}
        />
        
        <BlogPostEditor
          post={currentPost}
          isOpen={isEditorOpen}
          onClose={() => setIsEditorOpen(false)}
          onSave={handleSavePost}
          mode={editorMode}
        />

        <BlogPostPreview
          post={currentPost as BlogPost}
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
        />
      </div>
    </MasterLicenseLayout>
  );
};

export default MasterLicenseBlogManagement;
